import { addAllPageItems, addSelected, removeAllPageItems, removeSelected } from './tableHelper';
import { FIRST_CURSOR_BOUNDARY } from '../../api/cursorPagination';

import type { Reducer } from 'react';

import type { SnackbarInput } from '@/components/NewUI/ComponentsLibrary';

import type {
  CursorAction,
  GenericTableData,
  TableActionDrawerAction,
  TableActionItemAction,
  TableColumnAction,
  TablePaginationAction,
  TableRowRemoveSelectedAction,
  TableRowSelectedAction,
  TableScrollAction,
  TableSelectionAction,
  TPaginationState,
  TSortableColumn,
  TTableState
} from './types';
import type { CursorBoundary } from '../../api/cursorPagination';

export interface GenericTableState {
  searchQuery: string;
  searchSettings: TPaginationState<string>;
  tableState: TTableState<GenericTableData, TSortableColumn[]>;
  selectedRowTitle: null | TableRowSelectedAction['payload'];
  snackbarState: SnackbarInput;
}

// Request parameters for a numbered list: the pagination state without the client-only
// cursor `navigation`, which must never be sent as a query parameter.
export const numberedPaginationParams = <S,>({
  navigation,
  ...params
}: TPaginationState<S>): Omit<TPaginationState<S>, 'navigation'> => params;

export const initialTableState = (
  columns: TSortableColumn[],
  defaultSort: { sortBy: string; sortOrder: 'asc' | 'desc' },
  actionItems?: Record<string, boolean>,
  modals?: Record<string, boolean>
): GenericTableState => ({
  searchQuery: '',
  searchSettings: {
    page: 1,
    limit: 10,
    sort: defaultSort.sortBy,
    sort_order: defaultSort.sortOrder
  },
  tableState: {
    canScroll: [false, true],
    tableHovered: false,
    density: 'Default',
    columns,
    selectedItems: [],
    selectedItemIds: [],
    actions: {
      selectedItem: null,
      anchorEl: null,
      isOpen: {
        ...(actionItems ?? {})
      }
    },
    modalsOpen: {
      ...(modals ?? {})
    }
  },
  snackbarState: {
    message: '',
    state: 'success'
  },
  selectedRowTitle: null
});

export type GenericTableAction<T = GenericTableData> =
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_DEFAULT_DATA'; payload: GenericTableState }
  | { type: 'SET_SNACKBAR'; payload: SnackbarInput }
  | TableScrollAction
  | TableSelectionAction<T>
  | TableColumnAction<TSortableColumn[]>
  | TablePaginationAction<string>
  | TableActionItemAction<T>
  | TableActionDrawerAction
  | TableRowSelectedAction
  | TableRowRemoveSelectedAction;

type SearchSettings = GenericTableState['searchSettings'];

const isCursorMode = (settings: SearchSettings): boolean => settings.navigation?.mode === 'cursor';

// Cursor mode only: a new query, sort, page size or filter restarts from the first page, so
// a request never pairs a new query with an old token. Numbered settings are returned as-is.
const restartCursorTraversal = (settings: SearchSettings): SearchSettings =>
  isCursorMode(settings)
    ? { ...settings, page: 1, navigation: { mode: 'cursor', boundary: FIRST_CURSOR_BOUNDARY } }
    : settings;

const visitedPage = (page: number, boundary: CursorBoundary): number => {
  if (boundary.kind === 'after') return page + 1;
  if (boundary.kind === 'before') return Math.max(1, page - 1);
  return 1;
};

const cursorReducer = (settings: SearchSettings, action: CursorAction): SearchSettings => {
  switch (action.type) {
    case 'SET_CURSOR':
      if (!isCursorMode(settings)) return settings;
      return {
        ...settings,
        page: visitedPage(settings.page, action.payload),
        navigation: { mode: 'cursor', boundary: action.payload }
      };
    case 'SET_PAGINATION_MODE':
      if ((settings.navigation?.mode ?? 'numbered') === action.payload) return settings;
      return {
        ...settings,
        page: 1,
        navigation:
          action.payload === 'cursor'
            ? { mode: 'cursor', boundary: FIRST_CURSOR_BOUNDARY }
            : { mode: 'numbered' }
      };
    case 'RESET_PAGINATION':
      return restartCursorTraversal({ ...settings, page: 1 });
    default:
      return settings;
  }
};

export const genericTableReducer: Reducer<GenericTableState, GenericTableAction> = (
  state: GenericTableState,
  action: GenericTableAction
) => {
  switch (action.type) {
    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        searchQuery: action.payload,
        searchSettings: restartCursorTraversal({
          ...state.searchSettings,
          page: 1
        })
      };
    case 'ADD_SELECTED_ITEM':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          ...addSelected(
            state.tableState.selectedItems,
            state.tableState.selectedItemIds,
            action.payload
          )
        }
      };
    case 'SET_CAN_SCROLL':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          canScroll: action.payload
        }
      };
    case 'SET_TABLE_HOVERED':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          tableHovered: action.payload
        }
      };
    case 'REMOVE_SELECTED_ITEM':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          ...removeSelected(
            state.tableState.selectedItems,
            state.tableState.selectedItemIds,
            action.payload
          )
        }
      };
    case 'ADD_ALL_PAGE_ITEMS':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          ...addAllPageItems(state.tableState.selectedItems, action.payload)
        }
      };
    case 'REMOVE_ALL_PAGE_ITEMS':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          ...removeAllPageItems(state.tableState.selectedItems, action.payload)
        }
      };
    case 'SET_DENSITY':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          density: action.payload
        }
      };
    case 'SET_SORT':
      return {
        ...state,
        searchSettings: restartCursorTraversal({
          ...state.searchSettings,
          sort: action.payload.sortBy,
          sort_order: action.payload.sortOrder
        })
      };
    case 'SET_COLUMNS':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          columns: action.payload
        }
      };
    case 'SET_PAGE':
      // A cursor list has no random access: page 1 restarts it, other page numbers are ignored.
      if (isCursorMode(state.searchSettings)) {
        return action.payload === 1
          ? { ...state, searchSettings: restartCursorTraversal(state.searchSettings) }
          : state;
      }
      return {
        ...state,
        searchSettings: {
          ...state.searchSettings,
          page: action.payload
        }
      };
    case 'SET_LIMIT':
      return {
        ...state,
        searchSettings: restartCursorTraversal({
          ...state.searchSettings,
          limit: action.payload
        })
      };
    case 'SET_CURSOR':
    case 'SET_PAGINATION_MODE':
    case 'RESET_PAGINATION': {
      const searchSettings = cursorReducer(state.searchSettings, action);
      return searchSettings === state.searchSettings ? state : { ...state, searchSettings };
    }
    case 'SELECT_ACTION_ITEM':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          actions: {
            ...state.tableState.actions,
            selectedItem: action.payload.item,
            anchorEl: action.payload.anchorEl,
            name: action.payload.name,
            isOpen: {
              ...state.tableState.actions.isOpen,
              [action.payload.name]: true
            }
          }
        }
      };
    case 'CLOSE_ACTION_ITEM':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          actions: {
            ...state.tableState.actions,
            selectedItem: null,
            isOpen: {
              ...state.tableState.actions.isOpen,
              [action.payload]: false
            }
          }
        }
      };
    case 'SET_ACTION_MODAL_OPEN':
      return {
        ...state,
        tableState: {
          ...state.tableState,
          modalsOpen: {
            ...state.tableState.modalsOpen,
            [action.payload.name]: action.payload.isOpen
          }
        }
      };
    case 'SET_DEFAULT_DATA':
      return {
        ...action.payload
      };
    case 'SET_SELECTED_ROW_DATA':
      return {
        ...state,
        selectedRowTitle: { ...action.payload }
      };
    case 'REMOVE_SELECTED_ROW_DATA':
      return {
        ...state,
        selectedRowTitle: null
      };
    case 'SET_SNACKBAR':
      return {
        ...state,
        snackbarState: action.payload
      };
    default:
      return state;
  }
};
